import React, { useEffect, useRef, useState } from "react";

const LazyImage = ({
                       src,
                       alt,
                       className,
                       wrapperClassName,
                       fallbackSrc,
                       srcSet,
                       sizes,
                       width,
                       height,
                       fetchpriority = "low",
                       onLazyLoad,
                   }) => {
    const wrapperRef = useRef(null);
    const imgRef = useRef(null);

    const prevSrcRef = useRef({ src, fallbackSrc });
    const OBSERVER_ROOT_MARGIN = "300px";
    const OBSERVER_THRESHOLD = 0.01;

    const [inView, setInView] = useState(() => !("IntersectionObserver" in window));
    const [usedFallback, setUsedFallback] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);

    if (prevSrcRef.current.src !== src || prevSrcRef.current.fallbackSrc !== fallbackSrc) {
        prevSrcRef.current = { src, fallbackSrc };
        if (hasLoaded) setHasLoaded(false);
        if (usedFallback) setUsedFallback(false);
    }

    const currentSrc = inView ? (usedFallback ? fallbackSrc : src) : undefined;
    const activeSrcSet = inView && !usedFallback ? srcSet : undefined;
    const activeSizes = inView && !usedFallback ? sizes : undefined;

    useEffect(() => {
        if (!wrapperRef.current) return;
        if (!("IntersectionObserver" in window)) return;

        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        setInView(true);
                        obs.disconnect();
                    }
                });
            },
            { rootMargin: OBSERVER_ROOT_MARGIN, threshold: OBSERVER_THRESHOLD }
        );

        obs.observe(wrapperRef.current);
        return () => obs.disconnect();
    }, []);

    const handleError = () => {
        if (fallbackSrc && !usedFallback) {
            setUsedFallback(true);
        }
    };

    const handleLoad = () => {
        if (!hasLoaded) {
            setHasLoaded(true);
            if (onLazyLoad) onLazyLoad();
        }
    };

    return (
        <div
            ref={wrapperRef}
            className={wrapperClassName}
            style={{ width: width || "100%", height }}
        >
            <img
                ref={imgRef}
                className={className}
                src={currentSrc}
                srcSet={activeSrcSet}
                sizes={activeSizes}
                alt={alt}
                loading="lazy"
                decoding="async"
                width={width}
                height={height}
                fetchpriority={fetchpriority}
                onError={handleError}
                onLoad={handleLoad}
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                }}
            />
        </div>
    );
};

export default LazyImage;
